/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { createLogger } from '@/lib/logger';

const logger = createLogger();

/** DOM id of the injected Sierra embed `<script>`, used to dedupe injection. */
export const SIERRA_SCRIPT_ID = 'sierra-chat-embed';

/** Session-storage key holding the active Sierra conversation id (mirrors the SFRA implementation). */
export const PERSISTED_CONVERSATION_ID_KEY = 'persistedConversationID';

/** Context variables sent to the Sierra agent via `window.sierraConfig.variables`. */
export type SierraVariables = {
    brand: string;
    locale: string;
    source: string;
    contentId: string;
    contentType: string;
};

/** The subset of the Sierra runtime API this integration calls. */
export interface SierraApi {
    init: (config: unknown) => void;
    cleanup: () => Promise<void> | void;
    updateVariables: (vars: Partial<SierraVariables>) => void;
    getConversationId?: () => string | undefined;
    /** Open the chat modal (verified present on the live embed). */
    openChatModal?: () => Promise<void> | void;
    /** Close the chat modal. */
    closeChatModal?: () => Promise<void> | void;
    /** Whether the embed has finished initializing. */
    isInitialized?: () => boolean;
}

declare global {
    interface Window {
        /** Config object read by the Sierra embed on load; built by `<SierraChat>` before the embed script runs. */
        sierraConfig?: Record<string, unknown>;
        /** Sierra runtime API, present once the embed module has initialized. */
        sierra?: SierraApi;
        /** Fallback holder for the active conversation id when sessionStorage is unavailable. */
        sierraPersistedConversationID?: string;
        /**
         * Adobe Client Data Layer (also declared in src/analytics/adobe-data-layer.tsx).
         * Sierra lifecycle events are pushed here so Adobe Launch rules can consume them.
         */
        adobeDataLayer?: Array<Record<string, unknown>>;
    }
}

/**
 * Push a Sierra lifecycle event onto the Adobe Client Data Layer. Defensive: creates the
 * array if the `<head>` bootstrap (root.tsx) has not run, and no-ops during SSR.
 */
export function pushDataLayer(event: Record<string, unknown>): void {
    if (typeof window === 'undefined') return;
    const dataLayer = (window.adobeDataLayer = window.adobeDataLayer ?? []);
    dataLayer.push(event);
}

/** Read the active conversation id from sessionStorage, the window fallback, or the Sierra API. */
export function getPersistedConversationID(): string | undefined {
    if (typeof window === 'undefined') return undefined;

    let stored: string | undefined;
    try {
        stored = window.sessionStorage.getItem(PERSISTED_CONVERSATION_ID_KEY) ?? undefined;
    } catch {
        // sessionStorage can throw in privacy modes / sandboxed iframes — ignore.
    }

    return stored ?? window.sierraPersistedConversationID ?? window.sierra?.getConversationId?.();
}

/** Persist the active conversation id to sessionStorage and the window fallback. */
export function setPersistedConversationID(id: string): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.setItem(PERSISTED_CONVERSATION_ID_KEY, id);
    } catch {
        // ignore — the window fallback below still holds it for the tab.
    }
    window.sierraPersistedConversationID = id;
}

/** Clear any persisted conversation id (on conversation start/end). */
export function clearPersistedConversationID(): void {
    if (typeof window === 'undefined') return;
    try {
        window.sessionStorage.removeItem(PERSISTED_CONVERSATION_ID_KEY);
    } catch {
        // ignore
    }
    delete window.sierraPersistedConversationID;
}

/**
 * Open the Sierra chat modal programmatically (UCR-2926). Used by the chat deep-link interceptor when a
 * content-authored CTA (`resolveLinkHref` → {@link CHAT_LINK_HREF}) is clicked. Fail-soft: if the embed
 * hasn't loaded yet (disabled, blocked, or still initializing) it no-ops with a warning rather than
 * throwing — the widget is a third-party module that may not be present.
 *
 * No journey/re-init dance (unlike the SFRA `openChatFlow`): Black Magic opens the chat as-is.
 */
export async function openSierraChat(): Promise<void> {
    if (typeof window === 'undefined') return;
    const sierra = window.sierra;
    if (!sierra || typeof sierra.openChatModal !== 'function') {
        logger.warn('openSierraChat: chat embed not ready');
        return;
    }
    try {
        await sierra.openChatModal();
    } catch (error) {
        logger.error('openSierraChat failed', { error });
    }
}
